import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Chip, InputAdornment } from "@mui/material";
import { Search, Settings, ArrowLeft, Plus } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import Header from "../../../../components/Header";
import Container from "../../../../componentsv2/Container";
import Paper from "../../../../componentsv2/Paper";
import Button from "../../../../componentsv2/Button";
import Box from "../../../../componentsv2/Box";
import { Input } from "../../../../componentsv2/Input";
import TabelaV2 from "../../../../componentsv2/Tabela";
import ModalProcedimento from "../ModalProcedimento";
import { Context } from "../../../../Context/PermissoesContext";
import { api } from "../../../../services/Api";
import ModalConfirmacaoAcao from "../../Components/ModalConfirmacaoAcao";
import ModalOpcoesAtendimento from "./components/ModalOpcoesAtendimento";
import { CORES_TIPO } from "../../utils/coresTipoProcedimento";

const TIPOS_ATENDIMENTO = ["PROCEDIMENTO", "MEDICAMENTO", "ADICIONAL", "DESLOCAMENTO"];

export default function Atendimentos() {
  const history = useHistory();
  const { checarPermissao } = useContext(Context);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState("TODOS");
  const [statusFiltro, setStatusFiltro] = useState("TODOS");
  const [busca, setBusca] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [procedimentoEmEdicao, setProcedimentoEmEdicao] = useState(null);
  const [openModalOpcoes, setOpenModalOpcoes] = useState(false);
  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [rowSelecionada, setRowSelecionada] = useState(null);

  const podeVerPreco = checarPermissao("AGC002");
  const podeGerenciarCadastros = checarPermissao("AGC004");

  const carregarAtendimentos = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await api.get("/v2/agendaCare/procedimentos", {
        params: {
          status: statusFiltro,
          tipo: tipoFiltro === "TODOS" ? undefined : tipoFiltro,
        },
      });
      setRows(data || []);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar procedimentos!", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [statusFiltro, tipoFiltro]);

  useEffect(() => {
    if (!podeGerenciarCadastros) {
      return;
    }
    carregarAtendimentos();
  }, [podeGerenciarCadastros, carregarAtendimentos]);

  const abrirCadastro = () => {
    setProcedimentoEmEdicao(null);
    setOpenModal(true);
  };

  const abrirEdicao = procedimento => {
    setProcedimentoEmEdicao(procedimento);
    setOpenModal(true);
  };

  const rowsFiltradas = useMemo(() => {
    return rows.filter(row => {
      const texto = `${row.nome || ""} ${row.descricao || ""}`.toLowerCase();
      return !busca.trim() || texto.includes(busca.toLowerCase());
    });
  }, [rows, busca]);

  const abrirMenuOpcoes = row => {
    setRowSelecionada(row);
    setOpenModalOpcoes(true);
  };

  const fecharMenuOpcoes = () => {
    setOpenModalOpcoes(false);
    setRowSelecionada(null);
  };

  const executarEdicao = () => {
    if (!rowSelecionada) {
      return;
    }
    abrirEdicao(rowSelecionada);
    fecharMenuOpcoes();
  };

  const executarAlteracaoStatus = async () => {
    if (!rowSelecionada) {
      return;
    }

    try {
      await api.put(`/v2/agendaCare/procedimento/${rowSelecionada.id}`, {
        nome: rowSelecionada.nome,
        precoExame: rowSelecionada.precoExame,
        descricao: rowSelecionada.descricao,
        tipo: rowSelecionada.tipo,
        ativo: !rowSelecionada.ativo,
      });
      enqueueSnackbar("Status do procedimento atualizado com sucesso!", { variant: "success" });
      carregarAtendimentos();
    } catch (error) {
      enqueueSnackbar("Erro ao alterar status do procedimento!", { variant: "error" });
    }

    setOpenModalConfirmacao(false);
    fecharMenuOpcoes();
  };

  const abrirConfirmacaoStatus = () => {
    if (!rowSelecionada) {
      return;
    }
    setOpenModalConfirmacao(true);
  };

  const columns = useMemo(
    () => [
      {
        field: "tipo",
        headerName: "Tipo",
        width: 170,
        renderCell: ({ row }) => (
          <Chip
            label={row.tipo}
            size="small"
            sx={{
              backgroundColor:
                CORES_TIPO[row.tipo]?.backgroundColor || CORES_TIPO.PROCEDIMENTO.backgroundColor,
              color: CORES_TIPO[row.tipo]?.color || CORES_TIPO.PROCEDIMENTO.color,
              fontWeight: 700,
              fontSize: "10px",
              textTransform: "uppercase",
              borderRadius: "6px",
            }}
          />
        ),
      },
      {
        field: "nome",
        headerName: "Nome",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "precoExame",
        headerName: "Valor",
        width: 140,
        hide: !podeVerPreco,
        renderCell: ({ value }) =>
          Number(value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: ({ row }) => (
          <Chip
            label={row.ativo ? "Ativo" : "Inativo"}
            size="small"
            sx={{
              backgroundColor: row.ativo ? "#e9ffe8" : "#f1f3f5",
              color: row.ativo ? "#2d9d40" : "#8a919b",
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        field: "opcoes",
        headerName: "Opções",
        width: 90,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: params => (
          <Button
            variant="standard"
            onClick={() => abrirMenuOpcoes(params.row)}
            style={{ minWidth: "unset", padding: "0 6px" }}
          >
            <Settings size={18} color="#7c8798" />
          </Button>
        ),
      },
    ],
    [podeVerPreco]
  );

  if (!podeGerenciarCadastros) {
    return (
      <>
        <Header titulo="Atendimentos" />
        <Container>
          <Paper style={{ padding: "1rem", marginTop: "10px" }} elevation={2}>
            Você não tem permissão para acessar esta página.
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header titulo="Atendimentos" />
      <Container>
        <Button onClick={() => history.push("/agendaCare")} style={{ marginTop: "8px" }}>
          <ArrowLeft color="#003399" size={20} />
        </Button>
        {openModal && (
          <ModalProcedimento
            open={openModal}
            procedimento={procedimentoEmEdicao}
            onClose={() => {
              setOpenModal(false);
              carregarAtendimentos();
            }}
          />
        )}
        <Paper
          style={{
            padding: "1rem",
            marginTop: "10px",
          }}
          elevation={2}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "170px 170px minmax(280px, 1fr) 170px",
              },
              gap: 2,
              alignItems: "end",
            }}
          >
            <div>
              <Input
                select
                label="Tipo"
                fullWidth
                variant="standard"
                value={tipoFiltro}
                onChange={event => setTipoFiltro(event.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="TODOS">Todos</option>
                {TIPOS_ATENDIMENTO.map(tipo => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </Input>
            </div>
            <div>
              <Input
                select
                label="Status"
                fullWidth
                variant="standard"
                value={statusFiltro}
                onChange={event => setStatusFiltro(event.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="TODOS">Todos</option>
                <option value="ATIVO">Ativo</option>
                <option value="INATIVO">Inativo</option>
              </Input>
            </div>
            <div>
              <Input
                fullWidth
                label="Buscar"
                variant="standard"
                value={busca}
                onChange={event => setBusca(event.target.value)}
                placeholder="Procurar atendimento..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#7c8798" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={abrirCadastro}
                style={{ width: "100%" }}
                startIcon={<Plus size={16} />}
              >
                Atendimento
              </Button>
            </div>
          </Box>
          <Box sx={{ height: "600px", width: "100%", marginTop: "16px" }}>
            <TabelaV2
              rows={rowsFiltradas}
              columns={columns}
              loading={loading}
              autoPageSize
            />
          </Box>
        </Paper>
        <ModalOpcoesAtendimento
          open={openModalOpcoes}
          onClose={fecharMenuOpcoes}
          onEditar={executarEdicao}
          onAlternarStatus={abrirConfirmacaoStatus}
        />
        <ModalConfirmacaoAcao
          open={openModalConfirmacao}
          onCancelar={() => setOpenModalConfirmacao(false)}
          onConfirmar={executarAlteracaoStatus}
          titulo={rowSelecionada?.ativo ? "Inativar procedimento" : "Ativar procedimento"}
          descricao={
            rowSelecionada?.ativo
              ? "Deseja inativar este procedimento?"
              : "Deseja ativar este procedimento?"
          }
          textoConfirmar="Confirmar"
        />
      </Container>
    </>
  );
}
